import { IOfficeNew } from "../../../interfaces/models/IOffice";
import type {
  DataAdminView,
  SyncFirmAssignment,
} from "../../../interfaces/models/IDataAdmin";
import type { FirmContactView } from "../../../interfaces/models/IFirmContact";
import {
  MembershipType,
  ServiceType as OfficeServiceTypeValues,
} from "../../../Enums/OfficeEnum";
import { ServiceType as FirmServiceTypeValues } from "../../../Enums/FirmEnum";
import { isFirmContactAssigned } from "../../../utils/firmContactRoleConfig";
import {
  formatPersonWithMainOffice,
  toReadableLabel,
} from "../../../utils/contactOptionLabel";
import { formatProfessionalsOfficeFilterLabel } from "./professionalsOfficeFilterLabel";
import {
  getJurisdictionLabel,
  jurisdictionValueToLabel,
} from "../../../utils/jurisdiction/structuredJurisdiction";

export type ProfessionalPickerOption = { id: string; name: string };

export type DataAdminOfficeRow = {
  officeId: string;
  personId: string;
  rowKey: string;
  docId?: string;
  name?: string;
  firmEmail?: string;
  lastLogin?: number | null;
  generatedId?: number;
  legacyId?: string;
};

export type PersonOfficeLike = {
  id?: string | number;
  name?: string;
  lastName?: string;
  mainOffice?: string;
  mainOfficeName?: string;
  additionalOffices?: string[];
  isAttorney?: unknown;
  firmEmail?: string;
  generatedId?: number;
};

/** Firm-level values. Regional follows the same rules as Multiple Primary. */
const MULTIPLE_MEMBERSHIP_KEYS = new Set([
  "multiple",
  "multiple primary",
  "primary multiple",
  "regional",
]);

/** Firm-level membership. Office rows use lowercase keys such as "primary" and "multiple". */
export function isMultipleMembership(
  membershipType: string | undefined,
): boolean {
  const key = String(membershipType ?? "").trim().toLowerCase();
  return MULTIPLE_MEMBERSHIP_KEYS.has(key);
}

function isPrimaryOfficeMembership(office: IOfficeNew): boolean {
  const membershipType = String(office.membershipType ?? "")
    .trim()
    .toLowerCase();
  return membershipType === "primary";
}

function isMainOfficeType(office: IOfficeNew): boolean {
  return (
    String(office.officeType ?? "")
      .trim()
      .toLowerCase() === "main"
  );
}

function isSecondaryOfficeMembership(office: IOfficeNew): boolean {
  const membershipType = String(office.membershipType ?? "")
    .trim()
    .toLowerCase();
  return membershipType === "secondary";
}

/** Firm primary office used for data-admin assignments. */
export function getPrimaryOffice(
  offices: IOfficeNew[],
): IOfficeNew | undefined {
  if (offices.length === 0) {
    return undefined;
  }

  const byMembership = offices.find(isPrimaryOfficeMembership);
  if (byMembership) {
    return byMembership;
  }

  const byOfficeType = offices.find(isMainOfficeType);
  if (byOfficeType) {
    return byOfficeType;
  }

  return sortOfficesForProfileBlocks(offices)[0];
}

export function getPrimaryOfficeId(offices: IOfficeNew[]): string {
  return String(getPrimaryOffice(offices)?.id ?? "").trim();
}

export function isPrimaryFirmMembershipType(
  membershipType: string | undefined,
): boolean {
  return String(membershipType ?? "").trim() === "Primary";
}

/** Primary/Secondary/Accounting are required only on the primary office of a Primary, Multiple Primary, or Regional firm. */
export function requiresCoreFirmContacts(
  firmMembershipType: string | undefined,
  officeId: string,
  offices: IOfficeNew[],
): boolean {
  if (
    !isPrimaryFirmMembershipType(firmMembershipType) &&
    !isMultipleMembership(firmMembershipType)
  ) {
    return false;
  }

  if (offices.length === 0) {
    return true;
  }

  const primaryOfficeId = getPrimaryOfficeId(offices);
  const normalizedOfficeId = String(officeId ?? "").trim();
  if (!primaryOfficeId || !normalizedOfficeId) {
    return true;
  }

  return normalizedOfficeId === primaryOfficeId;
}

/**
 * Resolves which officeId to use when creating a data-admin from Add Professional.
 * - Main office is secondary → use that secondary office.
 * - Any other main office (primary, regional, multiple, etc.) → use firm primary office.
 */
export function resolveDataAdminOfficeIdForPerson(
  offices: IOfficeNew[],
  mainOfficeId: string | undefined,
): string {
  const normalizedMainOfficeId = String(mainOfficeId ?? "").trim();
  if (!normalizedMainOfficeId) {
    return getPrimaryOfficeId(offices);
  }

  const mainOffice = offices.find(
    (office) => String(office.id ?? "").trim() === normalizedMainOfficeId,
  );
  if (mainOffice && isSecondaryOfficeMembership(mainOffice)) {
    return normalizedMainOfficeId;
  }

  return getPrimaryOfficeId(offices);
}

/** Primary office first, then remaining offices by jurisdiction. */
export function sortOfficesForProfileBlocks(
  offices: IOfficeNew[],
): IOfficeNew[] {
  return [...offices].sort((left, right) => {
    const leftIsPrimary = isPrimaryOfficeMembership(left);
    const rightIsPrimary = isPrimaryOfficeMembership(right);
    if (leftIsPrimary && !rightIsPrimary) {
      return -1;
    }
    if (!leftIsPrimary && rightIsPrimary) {
      return 1;
    }

    return getJurisdictionLabel(left).localeCompare(
      getJurisdictionLabel(right),
      undefined,
      { sensitivity: "base" },
    );
  });
}

/** Offices visible in directory profile blocks (excludes showInDirectory=false). */
export function filterOfficesShownInDirectory(
  offices: IOfficeNew[],
): IOfficeNew[] {
  return offices.filter((office) => office.showInDirectory !== false);
}

export type FirmContactProfileOfficeOptions = {
  /** When true (WSG staff profiles), include offices with showInDirectory=false. */
  includeOfficesHiddenFromDirectory?: boolean;
};

/** Same office set as WSG Firm Contact Profile tabs and edit modal. */
export function resolveOfficesForFirmContactProfile(
  offices: IOfficeNew[],
  options?: FirmContactProfileOfficeOptions,
): IOfficeNew[] {
  if (options?.includeOfficesHiddenFromDirectory === true) {
    return offices;
  }
  return filterOfficesShownInDirectory(offices);
}

export function formatOfficeBlockHeading(office: IOfficeNew): string {
  const membership =
    MembershipType[office.membershipType as keyof typeof MembershipType] ?? "";
  const rawJurisdiction = toReadableLabel(getJurisdictionLabel(office));
  const place =
    rawJurisdiction ||
    (() => {
      const tab = formatOfficeTabLabel(office);
      return tab === "Office" ? "" : tab;
    })();
  if (membership && place) {
    return `${membership} - ${place}`;
  }
  return membership || place || "Office";
}

/** Office name for profile section tabs */
export function formatOfficeTabLabel(office: IOfficeNew): string {
  const candidates = [
    office.name,
    office.jurisdictionName,
    jurisdictionValueToLabel(office.jurisdiction),
  ];
  for (const candidate of candidates) {
    const label = toReadableLabel(candidate);
    if (label) {
      return label;
    }
  }

  const cityCountry = [office.cityName, office.countryName]
    .map((value) => toReadableLabel(value))
    .filter(Boolean)
    .join(", ");
  if (cityCountry) {
    return cityCountry;
  }
  return "Office";
}

function resolvePickerOfficeLabel(
  person: PersonOfficeLike,
  offices?: IOfficeNew[],
): string {
  const stored = toReadableLabel(person.mainOfficeName);
  if (stored) {
    return stored;
  }

  const mainOfficeId = String(person.mainOffice ?? "").trim();
  const office = offices?.find(
    (entry) => String(entry.id ?? "").trim() === mainOfficeId,
  );
  if (!office) {
    return "";
  }

  const fromTab = formatOfficeTabLabel(office);
  if (fromTab && fromTab !== "Office") {
    return fromTab;
  }

  return formatProfessionalsOfficeFilterLabel(office);
}

export function formatDataAdminPersonPickerName(
  person: PersonOfficeLike,
  offices?: IOfficeNew[],
): string {
  return formatPersonWithMainOffice(
    person,
    resolvePickerOfficeLabel(person, offices),
  );
}

function personBelongsToOffice(
  person: PersonOfficeLike,
  officeId: string,
): boolean {
  const oid = String(officeId ?? "").trim();
  if (!oid) {
    return false;
  }
  if (String(person.mainOffice ?? "").trim() === oid) {
    return true;
  }
  return (person.additionalOffices ?? []).some(
    (id) => String(id ?? "").trim() === oid,
  );
}

function personBelongsToAnyOffice(
  person: PersonOfficeLike,
  officeIds: string[],
): boolean {
  return officeIds.some((officeId) => personBelongsToOffice(person, officeId));
}

function buildSortedPickerOptions(
  persons: PersonOfficeLike[],
  include?: (person: PersonOfficeLike) => boolean,
): ProfessionalPickerOption[] {
  const seen = new Set<string>();
  const options: ProfessionalPickerOption[] = [];

  for (const person of persons) {
    if (include && !include(person)) {
      continue;
    }
    const id = String(person.id ?? "").trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    options.push({ id, name: formatPersonWithMainOffice(person) });
  }

  return options.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}

/** Normalizes office/firm serviceType keys or labels to a single comparable value. */
export function normalizeOfficeServiceType(value?: string | null): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed in OfficeServiceTypeValues) {
    return OfficeServiceTypeValues[
      trimmed as keyof typeof OfficeServiceTypeValues
    ];
  }

  if (trimmed in FirmServiceTypeValues) {
    return FirmServiceTypeValues[trimmed as keyof typeof FirmServiceTypeValues];
  }

  return trimmed;
}

function officesWithSameServiceType(
  offices: IOfficeNew[],
  serviceType?: string | null,
): IOfficeNew[] {
  const target = normalizeOfficeServiceType(serviceType);
  if (!target) {
    return [];
  }

  return offices.filter(
    (office) => normalizeOfficeServiceType(office.serviceType) === target,
  );
}

/** Offices that should have a firm-contact or data-admin block in the UI. */
export function getOfficesForFirmContactBlocks(
  offices: IOfficeNew[],
  membershipType?: string,
): IOfficeNew[] {
  if (offices.length === 0) {
    return [];
  }

  const sortedOffices = sortOfficesForProfileBlocks(offices);

  if (isMultipleMembership(membershipType)) {
    return sortedOffices;
  }

  return [sortedOffices[0]];
}

/** Office ids for firm-contact pickers: offices sharing the block's serviceType. */
export function getFirmContactAllowedOfficeIds(
  offices: IOfficeNew[],
  currentOfficeId: string,
): string[] {
  const currentId = String(currentOfficeId ?? "").trim();
  const currentOffice = offices.find(
    (office) => String(office.id ?? "").trim() === currentId,
  );
  if (!currentOffice) {
    return currentId ? [currentId] : [];
  }

  const sameServiceTypeOffices = officesWithSameServiceType(
    offices,
    currentOffice.serviceType,
  );
  if (sameServiceTypeOffices.length === 0) {
    return currentId ? [currentId] : [];
  }

  return [
    ...new Set(
      sameServiceTypeOffices
        .map((office) => String(office.id ?? "").trim())
        .filter(Boolean),
    ),
  ];
}

/** Firm-contact dropdown: persons linked to offices with the same serviceType. */
export function getPersonPickerOptionsForOffices(
  officeIds: string[],
  persons: PersonOfficeLike[],
  offices?: IOfficeNew[],
): ProfessionalPickerOption[] {
  return buildSortedPickerOptions(persons, (person) =>
    personBelongsToAnyOffice(person, officeIds),
  )
    .map((option) => {
      const person = persons.find(
        (entry) => String(entry.id ?? "").trim() === option.id,
      );

      return {
        ...option,
        name: person
          ? formatDataAdminPersonPickerName(person, offices)
          : option.name,
      };
    })
    .filter((option) => option.name.trim());
}

/** Data-admin dropdown: all professionals in the firm. */
export function getDataAdminPersonPickerOptions(
  persons: PersonOfficeLike[],
  offices?: IOfficeNew[],
): ProfessionalPickerOption[] {
  return buildSortedPickerOptions(persons)
    .map((option) => {
      const person = persons.find(
        (entry) => String(entry.id ?? "").trim() === option.id,
      );

      return {
        ...option,
        name: person
          ? formatDataAdminPersonPickerName(person, offices)
          : option.name,
      };
    })
    .filter((option) => option.name.trim());
}

const EMPTY_FIRM_CONTACT_PERSON: FirmContactView["primaryContact"] = {
  id: "",
  fullName: "",
  email: "",
  phone: { code: "", phone: "", basecode: "" },
};

function createEmptyFirmContactView(params: {
  firmId: string;
  firmName: string;
  officeId: string;
  jurisdiction: string;
}): FirmContactView {
  const empty = { ...EMPTY_FIRM_CONTACT_PERSON };
  return {
    firmId: params.firmId,
    officeId: params.officeId,
    firmName: params.firmName,
    jurisdiction: params.jurisdiction,
    primaryContact: { ...empty },
    secondaryContact: { ...empty },
    accountingContact: { ...empty },
    marketingContacts: [],
    liaisonContacts: [],
  };
}

/** Ensures one firm-contact block per applicable office. */
export function mergeFirmContactsWithOffices(
  firmContactData: FirmContactView[],
  offices: IOfficeNew[],
  firmId: string,
  firmName: string,
  membershipType?: string,
  profileOfficeOptions?: FirmContactProfileOfficeOptions,
): FirmContactView[] {
  if (!firmId) {
    return firmContactData;
  }

  const profileOffices = resolveOfficesForFirmContactProfile(
    offices,
    profileOfficeOptions,
  );
  const blocks = getOfficesForFirmContactBlocks(
    profileOffices,
    membershipType,
  );
  if (blocks.length === 0) {
    return firmContactData;
  }

  const existingByOfficeId = new Map(
    firmContactData.map((contact) => [
      String(contact.officeId ?? "").trim(),
      contact,
    ]),
  );

  return blocks.map((office) => {
    const officeId = String(office.id ?? "").trim();
    const existing = existingByOfficeId.get(officeId);
    if (existing) {
      return {
        ...existing,
        firmName: toReadableLabel(existing.firmName) || existing.firmName,
        jurisdiction: formatOfficeBlockHeading(office),
      };
    }

    return createEmptyFirmContactView({
      firmId,
      firmName: toReadableLabel(firmName || office.firmName) || firmName || office.firmName || "",
      officeId,
      jurisdiction: formatOfficeBlockHeading(office),
    });
  });
}

export const PARENT_PRIMARY_CONTACT_DEFAULT_NOTE =
  "No primary contact assigned. Defaulting to Main Office's primary contact.";

export type PropagateParentPrimaryContactResult = {
  contacts: FirmContactView[];
  defaultedOfficeIds: string[];
};

/** Fills empty child-jurisdiction primary contacts from the main office block. */
export function propagateParentPrimaryContactToChildJurisdictions(
  contacts: FirmContactView[],
  offices: IOfficeNew[],
  membershipType?: string,
): PropagateParentPrimaryContactResult {
  if (!isMultipleMembership(membershipType) || contacts.length <= 1) {
    return { contacts, defaultedOfficeIds: [] };
  }

  const primaryOfficeId = getPrimaryOfficeId(offices);
  if (!primaryOfficeId) {
    return { contacts, defaultedOfficeIds: [] };
  }

  const parentBlock = contacts.find(
    (contact) => String(contact.officeId ?? "").trim() === primaryOfficeId,
  );
  const parentPrimary = parentBlock?.primaryContact;
  if (!isFirmContactAssigned(parentPrimary)) {
    return { contacts, defaultedOfficeIds: [] };
  }

  const defaultedOfficeIds: string[] = [];
  const nextContacts = contacts.map((contact) => {
    const officeId = String(contact.officeId ?? "").trim();
    if (!officeId || officeId === primaryOfficeId) {
      return contact;
    }

    if (isFirmContactAssigned(contact.primaryContact)) {
      return contact;
    }

    defaultedOfficeIds.push(officeId);
    return {
      ...contact,
      primaryContact: { ...parentPrimary! },
    };
  });

  return { contacts: nextContacts, defaultedOfficeIds };
}

function copyPrimaryContactPerson(
  contact: FirmContactView["primaryContact"],
): FirmContactView["primaryContact"] {
  return {
    ...contact,
    phone: {
      code: contact.phone?.code ?? "",
      phone: contact.phone?.phone ?? "",
      basecode: contact.phone?.basecode ?? "",
    },
  };
}

/**
 * On the primary jurisdiction, an empty accounting or marketing slot
 * takes the primary contact. Assigned people in those slots stay put.
 */
export function applyPrimaryContactFallbacks(
  contact: FirmContactView,
  options: { requireCoreContacts: boolean },
): FirmContactView {
  if (!options.requireCoreContacts) {
    return contact;
  }
  if (!isFirmContactAssigned(contact.primaryContact)) {
    return contact;
  }

  const primaryContactId = String(contact.primaryContact.id ?? "").trim();
  if (!primaryContactId) {
    return contact;
  }

  const accountingMissing = !isFirmContactAssigned(contact.accountingContact);
  const marketingMissing =
    (contact.marketingContacts ?? []).filter(isFirmContactAssigned).length ===
    0;
  if (!accountingMissing && !marketingMissing) {
    return contact;
  }

  const primaryContact = copyPrimaryContactPerson(contact.primaryContact);
  return {
    ...contact,
    accountingContact: accountingMissing
      ? primaryContact
      : contact.accountingContact,
    marketingContacts: marketingMissing
      ? [copyPrimaryContactPerson(contact.primaryContact)]
      : contact.marketingContacts,
  };
}

export function expandDataAdminsToOfficeRows(
  docs: DataAdminView[],
): DataAdminOfficeRow[] {
  const rows: DataAdminOfficeRow[] = [];

  for (const doc of docs) {
    const personId = String(
      doc.personId ?? (doc as { person?: string }).person ?? "",
    ).trim();
    const officeId = String(doc.officeId ?? "").trim();
    if (!personId || !officeId) {
      continue;
    }

    rows.push({
      officeId,
      personId,
      rowKey: doc.id
        ? `${officeId}-${personId}-${doc.id}`
        : `${officeId}-${personId}`,
      docId: doc.id,
      name: doc.name ?? doc.personName,
      firmEmail: doc.firmEmail,
      lastLogin: doc.lastLogin,
      generatedId: doc.generatedId,
      legacyId: doc.legacyId,
    });
  }

  return rows;
}

/**
 * Primary jurisdiction must keep a data admin. When that office's tier has
 * none, the row is the primary contact — not whichever other contact exists.
 */
export function ensurePrimaryOfficeDataAdminRow(
  rows: DataAdminOfficeRow[],
  options: {
    offices: IOfficeNew[];
    membershipType?: string;
    primaryContact?: FirmContactView["primaryContact"];
  },
): DataAdminOfficeRow[] {
  const primaryOfficeId = getPrimaryOfficeId(options.offices);
  if (
    !primaryOfficeId ||
    !requiresCoreFirmContacts(
      options.membershipType,
      primaryOfficeId,
      options.offices,
    )
  ) {
    return rows;
  }

  const personId = String(options.primaryContact?.id ?? "").trim();
  if (!personId) {
    return rows;
  }

  const primaryTierHasDataAdmin = rows.some((row) => {
    if (!String(row.personId ?? "").trim()) {
      return false;
    }
    return (
      resolveDataAdminDisplayTier(row.officeId, options.offices) === "primary"
    );
  });
  if (primaryTierHasDataAdmin) {
    return rows;
  }

  return [
    ...rows,
    {
      officeId: primaryOfficeId,
      personId,
      rowKey: `primary-contact-fallback-${primaryOfficeId}-${personId}`,
      name: options.primaryContact?.fullName,
      firmEmail: options.primaryContact?.email,
    },
  ];
}

export type DataAdminOfficeBlock = {
  office: IOfficeNew;
  rows: DataAdminOfficeRow[];
};

export function collapseOfficeRowsToAssignments(
  rows: DataAdminOfficeRow[],
): SyncFirmAssignment[] {
  const assignments: SyncFirmAssignment[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const personId = String(row.personId ?? "").trim();
    const officeId = String(row.officeId ?? "").trim();
    if (!personId || !officeId) {
      continue;
    }

    const key = `${personId}:${officeId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    assignments.push({ personId, officeId });
  }

  return assignments;
}

export function getSecondaryOffice(
  offices: IOfficeNew[],
): IOfficeNew | undefined {
  return offices.find(isSecondaryOfficeMembership);
}

export function getSecondaryOfficeId(offices: IOfficeNew[]): string {
  return String(getSecondaryOffice(offices)?.id ?? "").trim();
}

export type DataAdminTier = "primary" | "secondary";

/** Maps a persisted officeId to the primary or secondary display group. */
export function resolveDataAdminDisplayTier(
  officeId: string,
  offices: IOfficeNew[],
): DataAdminTier {
  const normalizedOfficeId = String(officeId ?? "").trim();
  const secondaryOfficeId = getSecondaryOfficeId(offices);

  if (secondaryOfficeId && normalizedOfficeId === secondaryOfficeId) {
    return "secondary";
  }

  return "primary";
}

export function getDataAdminTierOfficeId(
  tier: DataAdminTier,
  offices: IOfficeNew[],
): string {
  if (tier === "secondary") {
    return getSecondaryOfficeId(offices);
  }

  return getPrimaryOfficeId(offices);
}

export function normalizeDataAdminRowsForSave(
  rows: DataAdminOfficeRow[],
  offices: IOfficeNew[],
): DataAdminOfficeRow[] {
  return rows.map((row) => {
    const tier = resolveDataAdminDisplayTier(row.officeId, offices);
    const officeId = getDataAdminTierOfficeId(tier, offices);
    if (!officeId) {
      return row;
    }
    return { ...row, officeId };
  });
}

/**
 * Groups data admins into primary and secondary blocks.
 * Unknown officeIds are shown under primary.
 */
export function mergeDataAdminBlocksByTier(
  docs: DataAdminView[],
  offices: IOfficeNew[],
): DataAdminOfficeBlock[] {
  const primaryOffice = getPrimaryOffice(offices);
  const secondaryOffice = getSecondaryOffice(offices);
  const expandedRows = expandDataAdminsToOfficeRows(docs);

  const primaryRows: DataAdminOfficeRow[] = [];
  const secondaryRows: DataAdminOfficeRow[] = [];

  for (const row of expandedRows) {
    if (resolveDataAdminDisplayTier(row.officeId, offices) === "secondary") {
      secondaryRows.push(row);
    } else {
      primaryRows.push(row);
    }
  }

  const blocks: DataAdminOfficeBlock[] = [];

  if (primaryOffice) {
    blocks.push({ office: primaryOffice, rows: primaryRows });
  }

  if (secondaryOffice) {
    blocks.push({ office: secondaryOffice, rows: secondaryRows });
  }

  return blocks;
}

/** Ensures one data-admin block per applicable office. */
export function mergeDataAdminBlocksWithOffices(
  docs: DataAdminView[],
  offices: IOfficeNew[],
  membershipType?: string,
): DataAdminOfficeBlock[] {
  const expandedRows = expandDataAdminsToOfficeRows(docs);
  const rowsByOfficeId = new Map<string, DataAdminOfficeRow[]>();

  for (const row of expandedRows) {
    const list = rowsByOfficeId.get(row.officeId) ?? [];
    list.push(row);
    rowsByOfficeId.set(row.officeId, list);
  }

  const officeById = new Map(
    offices.map((office) => [String(office.id ?? "").trim(), office]),
  );

  const baseBlockOffices = getOfficesForFirmContactBlocks(
    offices,
    membershipType,
  );
  const blockOfficeIds = new Set(
    baseBlockOffices
      .map((office) => String(office.id ?? "").trim())
      .filter(Boolean),
  );

  const extraOffices: IOfficeNew[] = [];
  for (const officeId of rowsByOfficeId.keys()) {
    if (!officeId || blockOfficeIds.has(officeId)) {
      continue;
    }

    const office = officeById.get(officeId);
    if (office) {
      extraOffices.push(office);
      continue;
    }

    const doc = docs.find(
      (entry) => String(entry.officeId ?? "").trim() === officeId,
    );
    if (!doc) {
      continue;
    }

    extraOffices.push({
      id: officeId,
      jurisdiction: doc.jurisdiction ?? "",
      membershipType: "",
    } as unknown as IOfficeNew);
  }

  const seenOfficeIds = new Set<string>();
  const allBlockOffices = sortOfficesForProfileBlocks([
    ...baseBlockOffices,
    ...extraOffices,
  ]).filter((office) => {
    const officeId = String(office.id ?? "").trim();
    if (!officeId || seenOfficeIds.has(officeId)) {
      return false;
    }
    seenOfficeIds.add(officeId);
    return true;
  });

  return allBlockOffices.map((office) => {
    const officeId = String(office.id ?? "").trim();
    return {
      office,
      rows: rowsByOfficeId.get(officeId) ?? [],
    };
  });
}
